import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { CubeQueryRequest, CubeQueryResult } from '../dtos/cube.types';
import { Query, ResultSet } from '@cubejs-client/core';
import { filter } from 'lodash';
import { filterOperators } from '../constants/playground.const';

/**
 * Service for interacting with the Cube API
 * Used to query aggregated data for analytics and visualizations
 */
@Injectable({
    providedIn: 'root'
})
export class CubeService extends HttpBaseService {
    private readonly baseEndpoint = 'api/Cube';

    /**
     * Execute a data cube query
     * @param queryRequest The query parameters and filters
     * @returns An Observable with the query results
     */
    executeQuery(queryRequest: Query): Observable<any> {
        const cleanedQuery = {
            ...queryRequest,
            filters: this.cleanFilters(queryRequest.filters || [])
        };
        return this.post<any>(`${this.baseEndpoint}/load`, cleanedQuery);
    }

    executeMultiQuery(query: Query): Observable<any[]> {
        const queries = this.splitQuery(query);
        const cleanedQueries = queries.map(queryRequest => ({
            ...queryRequest,
            filters: this.cleanFilters(queryRequest.filters || [])
        }));
        return this.post<any[]>(`${this.baseEndpoint}/load-multi`, cleanedQueries);
    }

    private cleanFilters(filters: any[]): any[] {
        return filter(filters, (filter) => {
            const operator = filter.operator;
            return !!operator && (['set', 'notSet'].includes(operator) || !!filter.values && filter.values.length > 0 &&
                filter.values.some(value => value !== null && value !== undefined && value !== ''));
        });
    }

    private splitQuery(query: Query): Query[] {
        const dimensions = query.dimensions || [];
        const measures = query.measures || [];
        const timeDimensions = query.timeDimensions || [];
        const filters = query.filters || [];
        const orders = query.order || {};
        const limit = query.limit;
        const offset = query.offset;

        return dimensions.map(dimension => ({
            dimensions: [dimension],
            measures: measures,
            timeDimensions: timeDimensions,
            filters: filters,
            order: orders,
            limit: limit,
            offset: offset
        }));
    }

    /**
     * Get available dimensions for a specific object definition
     * @param objectDefinitionId The ID of the object definition
     * @returns An Observable with the available dimensions
     */
    getAvailableDimensions(objectDefinitionId: string): Observable<string[]> {
        return this.get<string[]>(`${this.baseEndpoint}/dimensions/${objectDefinitionId}`);
    }

    /**
     * Get available measures for a specific object definition
     * @param objectDefinitionId The ID of the object definition
     * @returns An Observable with the available measures
     */
    getAvailableMeasures(objectDefinitionId: string): Observable<string[]> {
        return this.get<string[]>(`${this.baseEndpoint}/measures/${objectDefinitionId}`);
    }
}